import type { ElementType } from "react"

import { cn } from "@/lib/utils"

interface SegmentHeaderProps {
  label: string
  /** Flanking horizontal rules — broadcast lower-third style. Default true. */
  rules?: boolean
  /** Semantic element for the label. Default `h2`. */
  as?: ElementType
  /**
   * Size / tracking / alignment / margin overrides, tailwind-merged over the
   * defaults (`text-xs tracking-[0.3em]`). The gold color and canonical
   * dual-neon glow are fixed.
   */
  className?: string
}

// Canonical dual-neon heading glow (design-language: gold inner + green outer).
const HEADING_SHADOW =
  "0 0 12px oklch(0.85 0.28 85 / 0.5), 0 0 24px oklch(0.80 0.30 145 / 0.2)"
const RULE_GOLD = "oklch(0.85 0.28 85 / 0.3)"

/**
 * Gold dual-neon section label — broadcast lower-third style. With `rules`
 * (default) it is flanked by horizontal gradient rules for the homepage
 * broadcast; with `rules={false}` it renders the bare heading for reuse across
 * the match broadcast surfaces.
 */
export function SegmentHeader({
  label,
  rules = true,
  as: Tag = "h2",
  className,
}: SegmentHeaderProps) {
  const heading = (
    <Tag
      className={cn(
        "font-display text-xs font-bold tracking-[0.3em] uppercase",
        className,
      )}
      style={{
        color: "var(--neon-gold)",
        textShadow: HEADING_SHADOW,
      }}
    >
      {label}
    </Tag>
  )

  if (!rules) return heading

  return (
    <div className="flex items-center gap-4 px-4">
      {/* Left rule */}
      <div
        className="h-px flex-1"
        style={{
          background: `linear-gradient(90deg, transparent, ${RULE_GOLD})`,
        }}
      />

      {heading}

      {/* Right rule */}
      <div
        className="h-px flex-1"
        style={{
          background: `linear-gradient(90deg, ${RULE_GOLD}, transparent)`,
        }}
      />
    </div>
  )
}
